import {
  Divider as MuiDivider,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import React, { useState } from "react";
import { Hardware, License, Software } from "../../api/api-generated";
import {
  useFindAllHardwareForUserQuery,
  useFindLicensesForUserQuery,
  useFindSystemModuleLicensesForUserQuery,
  useFindUserForUserQuery,
} from "../../api/api-hooks";
import {
  downloadLicense,
  downloadSystemModule,
} from "../software-sold/licenseUtils";
import { useDownloadSpectrasLicenses } from "../software-sold/useDownloadSpectrasLicenses";
import useStyles from "./softwareDisplayStyles";

interface Props {
  softwareType: Software;
}

const LicenseDownloadDisplay = ({ softwareType }: Props) => {
  const { data: allHardware } = useFindAllHardwareForUserQuery({});
  const classes = useStyles();
  const { data: allLicenses } = useFindLicensesForUserQuery({});
  const { data: systemModules } = useFindSystemModuleLicensesForUserQuery();
  const { data: profile } = useFindUserForUserQuery({});
  const spectrasLicenseDownloader = useDownloadSpectrasLicenses();
  const [licenseError, setLicenseError] = useState<string>("");
  const [selectedHardware, setSelectedHardware] = useState<Omit<
    Hardware,
    "order" | "companies"
  > | null>(null);
  const [selectedLicenses, setSelectedLicenses] = useState<License[]>([]);
  if (
    allHardware === undefined ||
    allLicenses === undefined ||
    profile === undefined ||
    spectrasLicenseDownloader === undefined
  )
    return null;
  const filteredHardware = allHardware.filter((hardware) => {
    for (const license of hardware.licenses) {
      if (license.softwareId === softwareType.id) return true;
    }
    return false;
  });
  const handleHardwareChange = (
    event: any,
    autoboxValue: Omit<Hardware, "order" | "companies"> | null
  ) => {
    setSelectedHardware(autoboxValue);
    // If no hardware is selected...
    if (autoboxValue === null) {
      const matchingLicenses = allLicenses.filter(
        (license) => license.softwareId === softwareType.id
      );
      setSelectedLicenses(matchingLicenses);
      // If hardware is selected...
    } else {
      const matchingLicenses = allLicenses.filter(
        (license) =>
          license.softwareId === softwareType.id &&
          license.hardwareId === autoboxValue?.id
      );
      if (matchingLicenses.length === 0) {
        setLicenseError("No licenses available for the selected hardware.");
      } else {
        setLicenseError("");
      }
      setSelectedLicenses(matchingLicenses);
    }
  };
  const handleDownload = () => {
    if (!softwareType) return;
    if (softwareType.type.toLowerCase().includes("spectras")) {
      spectrasLicenseDownloader(selectedLicenses, profile.company.companyName);
    } else {
      downloadLicense(
        softwareType.type,
        profile.company.companyName,
        selectedLicenses
      );
    }
  };
  const handleDownloadSystemModules = () => {
    if (!systemModules) return;
    downloadSystemModule(systemModules, profile.company.companyName);
  };
  const handleDownloadAll = () => {
    if (!softwareType) return;
    const allLicensesForType = allLicenses.filter(
      (license) => license.softwareId === softwareType.id
    );
    if (softwareType.type.toLowerCase().includes("spectras")) {
      spectrasLicenseDownloader(
        allLicensesForType,
        profile.company.companyName
      );
    } else {
      downloadLicense(
        softwareType.type,
        profile.company.companyName,
        allLicensesForType
      );
    }
  };
  return (
    <Grid
      item
      container
      direction="column"
      alignItems="flex-start"
      spacing={2}
      className={classes.formContainer}
    >
      <Grid item>
        <Typography variant="h5">System-Level Licenses</Typography>
      </Grid>
      <Grid item>
        <Button
          onClick={handleDownloadSystemModules}
          variant="contained"
          color="primary"
        >
          Download All
        </Button>
      </Grid>
      <Grid item>
        <Typography variant="h5">Hardware-Based Licenses</Typography>
      </Grid>
      <Grid item>
        <Button onClick={handleDownloadAll} variant="contained" color="primary">
          Download for All Hardware
        </Button>
      </Grid>
      <Grid container item style={{ width: "300px" }}>
        <Grid container alignItems="center" spacing={3}>
          <Grid item xs>
            <MuiDivider />
          </Grid>
          <Grid item>
            <Typography variant="overline" align="center">
              Or
            </Typography>
          </Grid>
          <Grid item xs>
            <MuiDivider />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Autocomplete
          onChange={handleHardwareChange}
          value={selectedHardware}
          options={filteredHardware}
          getOptionLabel={(hardware) => hardware.serial}
          style={{ width: "300px" }}
          renderInput={(params) => (
            <TextField {...params} label="Select Hardware" variant="outlined" />
          )}
        />
      </Grid>
      <Grid item>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedHardware || !!licenseError}
          onClick={handleDownload}
        >
          Download
        </Button>
      </Grid>
      <Grid item>
        <Typography color="error">{licenseError}</Typography>
      </Grid>
    </Grid>
  );
};
export default LicenseDownloadDisplay;
